// Reports (IA §1 item 9, `…/reports/{topic}`; ANALYTICS-02/03/04), redesigned in the site's style
// (owner, 2026-10-22: "build Reports according to this website with attractive charts"):
//   * the section bar (Sales / Money / Operations / Visitors and traffic) and the reports of the group
//     as tabs, with the Money period pills and the Orders calendar, and Download CSV (q-bulk job);
//   * summary tiles with the change against the previous period and a small trend (click one to
//     chart it), the trend chart (the previous period dashed), the share by a breakdown (donut or
//     ranked bars), a topic card (money, profit, delivery, returns, stock, discounts, customers) and
//     a sortable, searchable details table.
// Every plan gets the full reports (owner, 2026-10-22). `…/reports/ranking` is ANALYTICS-05 and
// `…/traffic` the Visitors report with the consent mode.
import { useEffect, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { formatDateAD, formatDateTime, formatMoney } from "@ady/format";
import { t } from "@ady/i18n";
import { Banner, cx, EmptyState, Icon, Skeleton, useToast, type IconName } from "@ady/ui";
import { useIdempotencyKeys } from "../api/idempotency";
import { Sparkline } from "../charts/Charts";
import {
  BarList,
  CHART_COLOURS,
  Donut,
  Funnel,
  Ring,
  StackMeter,
  TrendChart,
  type TrendPoint,
} from "../charts/ReportCharts";
import { PERIODS, spanRange, type Span } from "../finance/money";
import { HeadButton, PeriodPills } from "../finance/ui";
import { ToggleRow } from "../marketing/ui";
import { CommercePage, useCan, useStoreIds } from "../orders/ui";
import { LoadError } from "../settings/SettingsPage";
import { AppLink } from "../shell/AppLink";
import { useShell } from "../shell/context";
import { can, findItem, hrefFor } from "../shell/nav";
import {
  TOPICS,
  analyticsApi,
  analyticsKeys,
  useLeaderboard,
  useReport,
  type Dim,
  type Report,
  type ReportExport,
  type ReportParams,
  type SeriesPoint,
  type Topic,
} from "./api";
import { addDays, formatFigure, isPlanLocked, keyLabel, metricLabel } from "./format";
import { ATID, ExportStatus, LeaderboardBand, PlanLocked, TrafficActivation } from "./ReportView";
import {
  GROUPS,
  SECTION_ICON,
  VIEWS,
  bucketFor,
  bucketSeries,
  bucketValue,
  groupOf,
  type Bucket,
  type Section,
  type TileSpec,
} from "./topics";

export type { Section } from "./topics";

const card =
  "flex min-w-0 flex-col gap-3 rounded-panel border border-card bg-surface p-4 shadow-card";
const DAY = 86_400_000;
const span = (from: string, to: string) =>
  Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY) + 1;

function useSections(): Section[] {
  const canProfit = useCan("reports.profit.read");
  return [...TOPICS.filter((x) => x !== "traffic" && (x !== "profit" || canProfit)), "ranking"];
}
const sectionLabel = (s: Section) =>
  s === "ranking" ? t("analytics.leaderboard.title") : t(`analytics.topics.${s}`);

// ---------------------------------------------------------------- navigation

/** The Reports section bar (owner, 2026-10-21): Sales / Money / Operations (each opens its first
 *  report) and Visitors and traffic. Shown by the shell section bar on every Reports page;
 *  `current` is undefined on the Visitors and traffic page. */
export function ReportsSwitcher({ current }: { current?: Section }) {
  const { orgSlug, storeSlug } = useStoreIds();
  const { membership } = useShell();
  const sections = useSections();
  const active = current ? groupOf(current).id : null;
  const traffic = findItem("traffic");
  const tab = (on: boolean) =>
    cx(
      "flex min-h-control flex-none items-center gap-2 rounded-xs px-3 text-sm font-medium no-underline",
      on ? "ady-bg-brand-tint" : "ady-hover-brand text-fg-secondary",
    );
  return (
    <nav
      aria-label={t("analytics.reports.groupsLabel")}
      className="ady-scroll-quiet flex w-fit max-w-full gap-1 overflow-x-auto rounded-xs border border-card bg-surface p-1"
    >
      {GROUPS.map((g) => {
        const first = g.topics.find((x) => sections.includes(x));
        if (!first) return null;
        const on = g.id === active;
        return (
          <AppLink
            key={g.id}
            href={`/${orgSlug}/${storeSlug}/reports/${first}`}
            aria-current={on ? "page" : undefined}
            className={tab(on)}
          >
            <Icon name={g.icon} gradient={on} />
            <span className={on ? "ady-text-brand" : "ady-hover-label"}>
              {t(`analytics.reports.groups.${g.id}`)}
            </span>
          </AppLink>
        );
      })}
      {traffic && can(membership, traffic.anyOf) ? (
        <AppLink
          href={hrefFor(traffic, orgSlug, storeSlug)}
          aria-current={current ? undefined : "page"}
          className={tab(!current)}
        >
          <Icon name="eye" gradient={!current} />
          <span className={current ? "ady-hover-label" : "ady-text-brand"}>{t(traffic.label)}</span>
        </AppLink>
      ) : null}
    </nav>
  );
}

/** The reports of the current group, as the site's view tabs. */
function TopicTabs({ current }: { current: Section }) {
  const { orgSlug, storeSlug } = useStoreIds();
  const sections = useSections();
  // Visitors and traffic is its own section-bar tab, with no reports beside it.
  if (current === "traffic") return null;
  const list = groupOf(current).topics.filter((x) => sections.includes(x));
  return (
    <nav
      aria-label={t("analytics.reports.tabsLabel")}
      className="flex max-w-full flex-wrap gap-1 self-start rounded-sm border border-card bg-surface p-1"
    >
      {list.map((x) => {
        const on = x === current;
        return (
          <AppLink
            key={x}
            href={`/${orgSlug}/${storeSlug}/reports/${x}`}
            aria-current={on ? "page" : undefined}
            className={cx(
              "flex min-h-control items-center gap-2 rounded-sm px-3 text-sm font-medium no-underline",
              on ? "ady-bg-brand-tint" : "text-fg-tertiary hover:bg-hover hover:text-fg",
            )}
          >
            <Icon name={SECTION_ICON[x]} gradient={on} />
            <span className={on ? "ady-text-brand" : undefined}>{sectionLabel(x)}</span>
          </AppLink>
        );
      })}
    </nav>
  );
}

/** Small chips (a chart metric, a breakdown). */
function Chips<T extends string>({
  label,
  items,
  value,
  onChange,
}: {
  label: string;
  items: { id: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div role="group" aria-label={label} className="flex flex-wrap gap-1">
      {items.map((i) => (
        <button
          key={i.id}
          type="button"
          aria-pressed={i.id === value}
          onClick={() => onChange(i.id)}
          className={cx(
            "flex min-h-7 items-center rounded-full border px-2.5 text-xs font-medium whitespace-nowrap",
            i.id === value
              ? "ady-outline-brand font-semibold text-link"
              : "border-card bg-surface text-fg-secondary hover:bg-hover",
          )}
        >
          {i.label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- download

function ExportButton({ topic, params }: { topic: Topic; params: ReportParams }) {
  const { orgId, storeId } = useStoreIds();
  const keys = useIdempotencyKeys();
  const toast = useToast();
  const [job, setJob] = useState<ReportExport | null>(null);
  const start = useMutation({
    mutationFn: () =>
      analyticsApi.exportReport(orgId, storeId, topic, params, keys({ topic, ...params })),
    onSuccess: (e) => {
      setJob(e);
      toast.show({ tone: "success", title: t("analytics.export.queued") });
    },
    onError: () => toast.show({ tone: "error", title: t("analytics.export.failed") }),
  });
  const status = useQuery({
    queryKey: ["analytics", orgId, storeId, "export", job?.id ?? ""],
    queryFn: () => analyticsApi.exportStatus(orgId, storeId, job!.id),
    enabled: !!job,
    refetchInterval: (q) =>
      q.state.data?.state === "done" || q.state.data?.state === "failed" ? false : 2000,
  });
  const current = status.data ?? job;
  // The file downloads by itself once it is ready (the link stays in the status too).
  const url = current?.state === "done" ? current.download?.url : undefined;
  useEffect(() => {
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.download = `${topic}-${params.from ?? ""}-${params.to ?? ""}.csv`;
    a.click();
  }, [url, topic, params.from, params.to]);
  return (
    <div className="flex flex-col items-end gap-1" data-testid={ATID.export}>
      <HeadButton
        icon="download"
        label={start.isPending ? t("analytics.export.preparing") : t("analytics.export.button")}
        onClick={() => start.mutate()}
        disabled={start.isPending}
      />
      {current ? <ExportStatus exp={current} /> : null}
    </div>
  );
}

// ---------------------------------------------------------------- tiles

function Delta({
  now,
  before,
  invert,
}: {
  now: number;
  before: number | undefined;
  invert?: boolean;
}) {
  if (before === undefined) return null;
  if (before === 0)
    return (
      <span className="text-fg-tertiary">
        {now === 0 ? t("analytics.reports.samePrevious") : t("analytics.reports.newPrevious")}
      </span>
    );
  const pct = Math.round(((now - before) / Math.abs(before)) * 1000) / 10;
  if (pct === 0)
    return <span className="text-fg-tertiary">{t("analytics.reports.samePrevious")}</span>;
  const good = invert ? pct < 0 : pct > 0;
  return (
    <span
      className={cx(
        "flex items-center gap-0.5 font-semibold",
        good ? "text-success-fg" : "text-danger-fg",
      )}
    >
      <span className={pct > 0 ? "-rotate-90" : "rotate-90"}>
        <Icon name="arrow" />
      </span>
      {t("analytics.reports.vsPrevious", { pct: `${pct > 0 ? "+" : ""}${pct}` })}
    </span>
  );
}

function ReportTile({
  spec,
  report,
  trend,
  active,
  onClick,
}: {
  spec: TileSpec;
  report: Report;
  trend: number[] | null;
  active: boolean;
  onClick?: () => void;
}) {
  const v = report.totals[spec.metric] ?? 0;
  const money = spec.metric.endsWith("_paisa");
  const body = (
    <>
      <span className="flex items-center gap-1.5 text-xs font-semibold text-fg-secondary">
        <span
          className={cx(
            "flex size-6 flex-none items-center justify-center rounded-xs",
            spec.tone === "success"
              ? "bg-success-bg text-success-fg"
              : spec.tone === "danger"
                ? "bg-danger-bg text-danger-fg"
                : spec.tone === "warning"
                  ? "bg-warning-bg text-warning-fg"
                  : "bg-selected text-link",
          )}
        >
          <Icon name={spec.icon} />
        </span>
        <span className="truncate">{metricLabel(spec.metric)}</span>
      </span>
      {money ? (
        <span className="ady-ledger flex items-baseline gap-1 pt-1.5">
          <span className="text-xs font-medium text-fg-tertiary">{t("today.home.currency")}</span>
          <span className="text-title-sm font-bold">{formatMoney(v, { bare: true })}</span>
        </span>
      ) : (
        <span className="ady-ledger pt-1.5 text-title-sm font-bold">
          {formatFigure(spec.metric, v)}
        </span>
      )}
      <span className="min-h-4 pt-0.5 text-2xs">
        <Delta
          now={v}
          before={report.previous?.[spec.metric]}
          {...(spec.invert ? { invert: true } : {})}
        />
      </span>
      {trend && trend.length > 1 ? (
        <span className="pt-1">
          <Sparkline values={trend} variant="gradient" label={metricLabel(spec.metric)} />
        </span>
      ) : null}
    </>
  );
  const cls = cx(
    "flex min-w-0 flex-col rounded-card border bg-surface px-3.5 pt-3 pb-2.5 text-start shadow-card",
    active ? "ady-outline-brand" : "border-card",
  );
  return onClick ? (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cx(cls, "hover:bg-hover")}
      data-metric={spec.metric}
    >
      {body}
    </button>
  ) : (
    <div className={cls} data-metric={spec.metric}>
      {body}
    </div>
  );
}

// ---------------------------------------------------------------- trend

const dayLabel = (d: string) => formatDateAD(`${d}T00:00:00`, "short");
function pointTitle(start: string, bucket: Bucket) {
  if (bucket === "day") return formatDateAD(`${start}T00:00:00`, "long");
  if (bucket === "week") return t("analytics.reports.weekOf", { date: dayLabel(start) });
  return new Date(`${start}T00:00:00Z`).toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}
function axisLabel(start: string, bucket: Bucket) {
  return bucket === "month"
    ? new Date(`${start}T00:00:00Z`).toLocaleDateString(undefined, {
        month: "short",
        timeZone: "UTC",
      })
    : dayLabel(start);
}

/** Money in rupees on the chart (paisa / 100); other figures as they are. */
const chartUnit = (metric: string, v: number) => (metric.endsWith("_paisa") ? v / 100 : v);

function TrendCard({
  report,
  prev,
  metric,
  metrics,
  onMetric,
}: {
  report: Report;
  prev: Report | undefined;
  metric: string;
  metrics: string[];
  onMetric: (m: string) => void;
}) {
  const bucket = bucketFor(report.series.length);
  const now = bucketSeries(report.series, bucket);
  const before = prev ? bucketSeries(prev.series, bucket) : [];
  const points: TrendPoint[] = now.map((b, i) => {
    const p = before[i];
    return {
      label: axisLabel(b.start, bucket),
      title: pointTitle(b.start, bucket),
      value: chartUnit(metric, bucketValue(b.points, metric)),
      ...(p ? { prev: chartUnit(metric, bucketValue(p.points, metric)) } : {}),
    };
  });
  const back = (v: number) => (metric.endsWith("_paisa") ? Math.round(v * 100) : v);
  const title = t("analytics.reports.overTime", { metric: metricLabel(metric) });
  return (
    <section className={card} aria-labelledby="rep-trend-h">
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex flex-col">
          <h2 id="rep-trend-h" className="text-lg font-semibold">
            {title}
          </h2>
          <span className="flex flex-wrap items-center gap-3 text-xs text-fg-tertiary">
            <span>{t(`analytics.reports.bucket.${bucket}`)}</span>
            <span className="flex items-center gap-1.5">
              <span className="ady-bg-brand h-1 w-4 rounded-full" />
              {t("analytics.reports.thisPeriod")}
            </span>
            {prev ? (
              <span className="flex items-center gap-1.5">
                <span className="w-4 border-t-2 border-dashed border-default" />
                {t("analytics.reports.prevPeriod")}
              </span>
            ) : null}
          </span>
        </div>
        {metrics.length > 1 ? (
          <Chips
            label={t("analytics.reports.chartMetric")}
            items={metrics.map((m) => ({ id: m, label: metricLabel(m) }))}
            value={metric}
            onChange={onMetric}
          />
        ) : null}
      </header>
      <TrendChart
        points={points}
        kind={points.length <= 14 ? "bars" : "area"}
        format={(v) => formatFigure(metric, back(v))}
        label={title}
        seriesLabel={t("analytics.reports.thisPeriod")}
        prevLabel={t("analytics.reports.prevPeriod")}
      />
    </section>
  );
}

// ---------------------------------------------------------------- share

function ShareCard({
  report,
  dims,
  dim,
  onDim,
  metric,
  donut,
}: {
  report: Report;
  dims: Dim[];
  dim: Dim;
  onDim: (d: Dim) => void;
  metric: string;
  donut: boolean;
}) {
  const value = (r: Report["rows"][number]) => Number(r[metric] ?? 0) || 0;
  const rows = report.rows.filter((r) => value(r) > 0);
  const top = rows.slice(0, donut ? 6 : 8);
  const rest = rows.slice(top.length).reduce((a, r) => a + value(r), 0);
  const slices = [
    ...top.map((r) => ({
      key: r.key,
      label: keyLabel(report.dim, r),
      value: value(r),
      display: formatFigure(metric, value(r)),
    })),
    ...(rest > 0
      ? [
          {
            key: "_rest",
            label: t("analytics.keys.other"),
            value: rest,
            display: formatFigure(metric, rest),
          },
        ]
      : []),
  ];
  const total = rows.reduce((a, r) => a + value(r), 0);
  return (
    <section className={card} aria-labelledby="rep-share-h">
      <header className="flex flex-col gap-2">
        <div className="flex flex-col">
          <h2 id="rep-share-h" className="text-lg font-semibold">
            {t("analytics.reports.byDim", { dim: t(`analytics.dims.${dim}`) })}
          </h2>
          <p className="text-xs text-fg-tertiary">
            {t("analytics.reports.shareLead", { metric: metricLabel(metric) })}
          </p>
        </div>
        {dims.length > 1 ? (
          <Chips
            label={t("analytics.reports.dim")}
            items={dims.map((d) => ({ id: d, label: t(`analytics.dims.${d}`) }))}
            value={dim}
            onChange={onDim}
          />
        ) : null}
      </header>
      {slices.length === 0 ? (
        <p className="py-8 text-center text-sm text-fg-tertiary">
          {t("analytics.reports.shareEmpty")}
        </p>
      ) : donut ? (
        <Donut
          slices={slices}
          total={formatFigure(metric, total)}
          centerLabel={metricLabel(metric)}
          label={t("analytics.reports.byDim", { dim: t(`analytics.dims.${dim}`) })}
        />
      ) : (
        <BarList
          rows={slices}
          label={t("analytics.reports.byDim", { dim: t(`analytics.dims.${dim}`) })}
        />
      )}
    </section>
  );
}

// ---------------------------------------------------------------- topic cards

function CardHead({ title, lead, aside }: { title: string; lead: string; aside?: ReactNode }) {
  return (
    <header className="flex flex-wrap items-start justify-between gap-2">
      <div className="flex flex-col">
        <h2 className="text-lg font-semibold">{title}</h2>
        <p className="text-xs text-fg-tertiary">{lead}</p>
      </div>
      {aside}
    </header>
  );
}

const rate = (bp: number | undefined) => (bp ?? 0) / 10_000;
const pctText = (bp: number | undefined) => formatFigure("x_bp", bp ?? 0);

function TopicCard({ report }: { report: Report }) {
  const { orgSlug, storeSlug } = useStoreIds();
  const v = (k: string) => report.totals[k] ?? 0;
  const money = (k: string, colour: string) => ({
    key: k,
    label: metricLabel(k),
    value: v(k),
    display: formatMoney(v(k)),
    colour,
  });
  switch (report.topic) {
    case "sales":
    case "payments":
      return (
        <section className={card}>
          <CardHead
            title={t("analytics.reports.moneyTitle")}
            lead={t("analytics.reports.moneyLead")}
          />
          <StackMeter
            label={t("analytics.reports.moneyTitle")}
            parts={[
              money("collected_paisa", "var(--ady-color-tone-success-fg)"),
              money("outstanding_paisa", "var(--ady-color-tone-warning-fg)"),
              money("refunded_paisa", "var(--ady-color-tone-danger-fg)"),
            ]}
          />
        </section>
      );
    case "profit": {
      const max = Math.max(1, v("revenue_paisa"));
      const bars: [string, string][] = [
        ["revenue_paisa", "ady-bg-brand"],
        ["cogs_paisa", "bg-warning-fg"],
        ["gross_profit_paisa", "bg-success-fg"],
      ];
      return (
        <section className={card}>
          <CardHead
            title={t("analytics.reports.profitTitle")}
            lead={t("analytics.reports.profitLead")}
          />
          <div className="grid items-center gap-5 md:grid-cols-[minmax(0,1fr)_auto]">
            <ul className="flex flex-col gap-2.5">
              {bars.map(([k, colour]) => (
                <li key={k} className="flex flex-col gap-1 text-sm">
                  <span className="flex justify-between gap-2">
                    <span>{metricLabel(k)}</span>
                    <span className={cx("ady-ledger font-semibold", v(k) < 0 && "text-danger-fg")}>
                      {formatMoney(v(k))}
                    </span>
                  </span>
                  <span className="h-2.5 overflow-hidden rounded-full bg-sunken">
                    <span
                      className={cx("block h-full rounded-full", colour)}
                      style={{ width: `${(Math.max(0, v(k)) / max) * 100}%` }}
                    />
                  </span>
                </li>
              ))}
            </ul>
            <Ring
              value={rate(v("margin_bp"))}
              display={pctText(v("margin_bp"))}
              label={metricLabel("margin_bp")}
              tone={v("margin_bp") >= 0 ? "success" : "danger"}
            />
          </div>
        </section>
      );
    }
    case "delivery":
      return (
        <section className={card}>
          <CardHead
            title={t("analytics.reports.deliveryTitle")}
            lead={t("analytics.reports.deliveryLead")}
          />
          <div className="flex flex-wrap gap-6">
            <Ring
              value={rate(v("delivery_rate_bp"))}
              display={pctText(v("delivery_rate_bp"))}
              label={metricLabel("delivery_rate_bp")}
              tone="success"
            />
            <Ring
              value={rate(v("rto_rate_bp"))}
              display={pctText(v("rto_rate_bp"))}
              label={metricLabel("rto_rate_bp")}
              tone="danger"
            />
            <div className="flex flex-col justify-center">
              <span className="text-xs font-medium text-fg-secondary">
                {metricLabel("average_delivery_minutes")}
              </span>
              <span className="ady-ledger text-title-sm font-bold">
                {formatFigure("average_delivery_minutes", v("average_delivery_minutes"))}
              </span>
            </div>
          </div>
        </section>
      );
    case "returns":
      return (
        <section className={card}>
          <CardHead
            title={t("analytics.reports.returnsTitle")}
            lead={t("analytics.reports.returnsLead")}
          />
          <div className="flex flex-wrap gap-6">
            <Ring
              value={rate(v("return_rate_bp"))}
              display={pctText(v("return_rate_bp"))}
              label={metricLabel("return_rate_bp")}
              tone="warning"
            />
            <Ring
              value={rate(v("rto_rate_bp"))}
              display={pctText(v("rto_rate_bp"))}
              label={metricLabel("rto_rate_bp")}
              tone="danger"
            />
          </div>
        </section>
      );
    case "inventory": {
      const cost = v("stock_value_cost_paisa");
      const profit = Math.max(0, v("stock_value_price_paisa") - cost);
      return (
        <section className={card}>
          <CardHead
            title={t("analytics.reports.stockTitle")}
            lead={t("analytics.reports.stockLead")}
            aside={
              <AppLink
                href={`/${orgSlug}/${storeSlug}/inventory`}
                className="text-xs font-semibold text-link no-underline hover:underline"
              >
                {t("analytics.reports.viewInventory")}
              </AppLink>
            }
          />
          <StackMeter
            label={t("analytics.reports.stockTitle")}
            parts={[
              {
                key: "cost",
                label: metricLabel("stock_value_cost_paisa"),
                value: cost,
                display: formatMoney(cost),
                colour: "var(--ady-color-chart-1)",
              },
              {
                key: "profit",
                label: t("analytics.reports.potentialProfit"),
                value: profit,
                display: formatMoney(profit),
                colour: "var(--ady-color-tone-success-fg)",
              },
            ]}
          />
          {v("cost_missing_units") > 0 ? (
            <p className="text-2xs text-fg-tertiary">
              {metricLabel("cost_missing_units")}:{" "}
              {formatFigure("cost_missing_units", v("cost_missing_units"))}
            </p>
          ) : null}
        </section>
      );
    }
    case "discounts": {
      const coupon = v("coupon_discount_paisa");
      const other = Math.max(0, v("discount_paisa") - coupon);
      return (
        <section className={card}>
          <CardHead
            title={t("analytics.reports.discountTitle")}
            lead={t("analytics.reports.discountLead")}
          />
          <div className="grid items-center gap-5 md:grid-cols-[minmax(0,1fr)_auto]">
            <StackMeter
              label={t("analytics.reports.discountTitle")}
              parts={[
                {
                  key: "coupon",
                  label: metricLabel("coupon_discount_paisa"),
                  value: coupon,
                  display: formatMoney(coupon),
                  colour: "var(--ady-color-chart-1)",
                },
                {
                  key: "other",
                  label: t("analytics.reports.otherDiscounts"),
                  value: other,
                  display: formatMoney(other),
                  colour: "var(--ady-color-chart-2)",
                },
              ]}
            />
            <Ring
              value={rate(v("discount_rate_bp"))}
              display={pctText(v("discount_rate_bp"))}
              label={metricLabel("discount_rate_bp")}
              tone="warning"
            />
          </div>
        </section>
      );
    }
    case "traffic": {
      const steps: [string, string][] = [
        ["view_to_cart_bp", "add_to_cart"],
        ["cart_to_checkout_bp", "checkout_start"],
        ["checkout_to_purchase_bp", "purchase"],
      ];
      return (
        <section className={card}>
          <CardHead
            title={t("analytics.traffic.funnel")}
            lead={t("analytics.traffic.funnelLead")}
          />
          <div className="grid items-center gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
            <Funnel
              label={t("analytics.traffic.funnel")}
              steps={
                // People at each step when the visitors read has them; else event counts.
                report.totals.visitors_product_view !== undefined
                  ? [
                      {
                        key: "visitors",
                        label: t("analytics.traffic.steps.visitors"),
                        value: v("visitors"),
                      },
                      ...(
                        ["product_view", "add_to_cart", "checkout_start", "purchase"] as const
                      ).map((k) => ({
                        key: k,
                        label: t(`analytics.traffic.steps.${k}`),
                        value: v(`visitors_${k}`),
                      })),
                    ]
                  : FUNNEL.map((k) => ({ key: k, label: metricLabel(k), value: v(k) }))
              }
              stepRate={(pct) => t("analytics.traffic.stepRate", { pct })}
            />
            <div className="flex flex-col gap-4">
              {steps.map(([k]) => (
                <Ring key={k} value={rate(v(k))} display={pctText(v(k))} label={metricLabel(k)} />
              ))}
            </div>
          </div>
        </section>
      );
    }
    default:
      return null;
  }
}

/** Customers: new against returning as a donut, and the repeat share. */
function CustomerMix({ report }: { report: Report }) {
  const v = (k: string) => report.totals[k] ?? 0;
  const slices = (["new_customers", "returning_customers"] as const).map((k) => ({
    key: k,
    label: metricLabel(k),
    value: v(k),
    display: formatFigure(k, v(k)),
  }));
  return (
    <section className={card}>
      <CardHead
        title={t("analytics.reports.customerMixTitle")}
        lead={t("analytics.reports.customerMixLead")}
      />
      <Donut
        slices={slices}
        total={formatFigure("customers", v("customers"))}
        centerLabel={metricLabel("customers")}
        label={t("analytics.reports.customerMixTitle")}
      />
      <Ring
        value={v("customers") ? v("repeat_customers") / v("customers") : 0}
        display={`${v("customers") ? Math.round((v("repeat_customers") * 100) / v("customers")) : 0}%`}
        label={t("analytics.reports.repeatRate")}
      />
    </section>
  );
}

// ---------------------------------------------------------------- details

type Filter = "all" | "unsold" | "low";

function DetailsTable({ report, metrics }: { report: Report; metrics: string[] }) {
  const [sort, setSort] = useState<{ by: string; desc: boolean } | null>(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const byRows = report.rows.length > 0;
  // Columns that are zero on every row (no tax, no cancellations) are left out.
  const cols = (
    byRows
      ? Object.keys(report.rows[0]!).filter((k) => k !== "key" && k !== "label")
      : metrics.filter((m) => report.series.length && m in report.series[0]!)
  ).filter((c) => (byRows ? report.rows : report.series).some((r) => Number(r[c] ?? 0) !== 0));
  const base: { id: string; label: string; get: (k: string) => number }[] = byRows
    ? report.rows.map((r) => ({
        id: r.key,
        label: keyLabel(report.dim, r),
        get: (k: string) => Number(r[k] ?? 0) || 0,
      }))
    : [...report.series].reverse().map((p: SeriesPoint) => ({
        id: String(p.date),
        label: formatDateAD(`${String(p.date)}T00:00:00`, "medium"),
        get: (k: string) => Number(p[k] ?? 0) || 0,
      }));
  const needle = search.trim().toLowerCase();
  const inv = report.topic === "inventory";
  let rows = base.filter((r) => !needle || r.label.toLowerCase().includes(needle));
  if (inv && filter === "unsold")
    rows = rows.filter((r) => r.get("units_sold") === 0 && r.get("stock_units") > 0);
  if (inv && filter === "low")
    rows = rows.filter((r) => r.get("low_stock_variants") + r.get("out_of_stock_variants") > 0);
  if (sort)
    rows = [...rows].sort((a, b) => (a.get(sort.by) - b.get(sort.by)) * (sort.desc ? -1 : 1));
  if (!base.length) return null;
  const head =
    report.topic === "customers"
      ? t("analytics.reports.customerCol")
      : byRows
        ? t(`analytics.dims.${report.dim}`)
        : t("analytics.dims.total");
  return (
    <section className={card} aria-labelledby="rep-tab-h">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-baseline gap-2">
          <h2 id="rep-tab-h" className="text-lg font-semibold">
            {t("analytics.reports.detailTitle")}
          </h2>
          <span className="text-xs text-fg-tertiary">
            {t("analytics.reports.rowsCount", { count: String(rows.length) })}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {inv ? (
            <Chips
              label={t("analytics.reports.detailTitle")}
              items={(["all", "unsold", "low"] as const).map((f) => ({
                id: f,
                label: t(
                  `analytics.reports.filter${f === "all" ? "All" : f === "unsold" ? "Unsold" : "Low"}`,
                ),
              }))}
              value={filter}
              onChange={setFilter}
            />
          ) : null}
          {byRows ? (
            <label className="relative flex items-center">
              <span className="pointer-events-none absolute start-2.5 text-fg-tertiary">
                <Icon name="search" />
              </span>
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("analytics.reports.search")}
                aria-label={t("analytics.reports.search")}
                className="min-h-control w-56 rounded-xs border border-control bg-surface ps-8 pe-3 text-sm placeholder:text-fg-tertiary"
              />
            </label>
          ) : null}
        </div>
      </header>
      <div className="ady-scroll-quiet overflow-x-auto">
        <table className="w-full text-sm" data-testid={ATID.table}>
          <caption className="ady-sr-only">{t("analytics.reports.detailTitle")}</caption>
          <thead>
            <tr className="border-b border-subtle text-2xs font-semibold text-fg-tertiary uppercase">
              <th scope="col" className="py-2 pe-3 text-start">
                {head}
              </th>
              {cols.map((c) => {
                const on = sort?.by === c;
                return (
                  <th
                    key={c}
                    scope="col"
                    className="py-2 ps-3 text-end whitespace-nowrap"
                    aria-sort={on ? (sort!.desc ? "descending" : "ascending") : "none"}
                  >
                    <button
                      type="button"
                      onClick={() => setSort({ by: c, desc: on ? !sort!.desc : true })}
                      className={cx(
                        "inline-flex items-center gap-1 uppercase hover:text-fg",
                        on && "text-link",
                      )}
                    >
                      {metricLabel(c)}
                      {on ? (
                        <span className={sort!.desc ? "" : "rotate-180"}>
                          <Icon name="chevronDown" />
                        </span>
                      ) : null}
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-subtle last:border-0 hover:bg-hover">
                <td className="py-2 pe-3">
                  <span className="flex items-center gap-2">
                    <span className="ady-user-content">{r.label}</span>
                    {inv && r.get("out_of_stock_variants") > 0 ? (
                      <span className="rounded-full bg-danger-bg px-1.5 text-2xs font-semibold text-danger-fg">
                        {metricLabel("out_of_stock_variants")}
                      </span>
                    ) : inv && r.get("low_stock_variants") > 0 ? (
                      <span className="rounded-full bg-warning-bg px-1.5 text-2xs font-semibold text-warning-fg">
                        {metricLabel("low_stock_variants")}
                      </span>
                    ) : null}
                  </span>
                </td>
                {cols.map((c) => (
                  <td
                    key={c}
                    className={cx(
                      "ady-ledger py-2 ps-3 text-end whitespace-nowrap",
                      r.get(c) < 0 && "text-danger-fg",
                    )}
                  >
                    {formatFigure(c, r.get(c))}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 ? (
          <p className="py-6 text-center text-sm text-fg-tertiary">
            {t("analytics.reports.noRows")}
          </p>
        ) : null}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------- page

/** The period the pages share: the Money pills and the Orders calendar (max 366 days). */
function usePeriod(only: readonly Span[] = PERIODS) {
  const [period, setPeriod] = useState<Span>("d30");
  const [custom, setCustom] = useState({ from: "", to: "" });
  const { from, to } = spanRange(period, custom);
  const pills = (
    <PeriodPills
      value={period}
      onChange={setPeriod}
      only={only}
      custom={custom}
      onDates={(r) => {
        if (r) {
          setCustom(r);
          setPeriod("custom");
        } else setPeriod("d30");
      }}
    />
  );
  return { from, to, pills, lifetime: period === "lifetime" };
}

function TopicReport({
  topic,
  from,
  to,
  pills,
  lifetime = false,
}: {
  topic: Topic;
  from: string;
  to: string;
  pills: ReactNode;
  /** Nothing comes before Lifetime: no previous period to draw. */
  lifetime?: boolean;
}) {
  const { orgId, storeId } = useStoreIds();
  const view = VIEWS[topic];
  const [dim, setDim] = useState<Dim>(view.dims[0]!);
  const [metric, setMetric] = useState(view.chart[0] ?? "");
  const [compare, setCompare] = useState(true);
  const params: ReportParams = { from, to, dim };
  const q = useReport(orgId, storeId, topic, params);
  const days = span(from, to);
  const prevParams: ReportParams = { from: addDays(from, -days), to: addDays(from, -1), dim };
  const p = useReport(
    orgId,
    storeId,
    topic,
    prevParams,
    compare && !lifetime && view.chart.length > 0 && !!q.data,
  );
  const report = q.data;
  const bucket = bucketFor(report?.series.length ?? 0);
  const spark = (m: string) =>
    report && report.series.length > 1 && m in report.series[0]!
      ? bucketSeries(report.series, bucket).map((b) => bucketValue(b.points, m))
      : null;
  const empty = report && Object.values(report.totals).every((x) => !x) && report.rows.length === 0;
  return (
    <>
      <div className="flex flex-wrap items-start gap-2">
        {topic === "traffic" ? pills : <TopicTabs current={topic} />}
        <div className="ms-auto flex flex-wrap items-start gap-3">
          {topic === "traffic" &&
          view.chart.length &&
          !lifetime &&
          report?.availability !== "activation_required" ? (
            <ToggleRow
              label={t("analytics.reports.compare")}
              checked={compare}
              onChange={setCompare}
            />
          ) : null}
          {report?.updated_at ? (
            <span className="pt-2 text-xs text-fg-tertiary">
              {t("analytics.reports.updated", { when: formatDateTime(report.updated_at) })}
            </span>
          ) : null}
          {report?.availability === "activation_required" ? null : (
            <ExportButton topic={topic} params={params} />
          )}
        </div>
      </div>
      <div className={cx("flex flex-wrap items-center gap-2", topic === "traffic" && "hidden")}>
        {pills}
        {view.chart.length && !lifetime && report?.availability !== "activation_required" ? (
          <div className="ms-auto">
            <ToggleRow
              label={t("analytics.reports.compare")}
              checked={compare}
              onChange={setCompare}
            />
          </div>
        ) : null}
      </div>
      {q.isError ? (
        <div className={card}>
          {isPlanLocked(q.error) ? (
            <PlanLocked />
          ) : (
            <LoadError error={q.error} onRetry={() => void q.refetch()} />
          )}
        </div>
      ) : !report ? (
        <div className="flex flex-col gap-3">
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
            {Array.from({ length: 5 }, (_, i) => (
              <Skeleton key={i} className="h-28 w-full" />
            ))}
          </div>
          <Skeleton className="h-72 w-full" />
        </div>
      ) : report.availability === "activation_required" ? (
        <div className={card}>
          <TrafficActivation />
        </div>
      ) : (
        <>
          {report.partial ? (
            <Banner
              kind="info"
              title={t("analytics.reports.partialProfit")}
              data-testid={ATID.partial}
            />
          ) : null}
          <div
            className={cx(
              "grid gap-3 sm:grid-cols-2",
              view.tiles.length >= 6
                ? "xl:grid-cols-6"
                : view.tiles.length === 5
                  ? "xl:grid-cols-5"
                  : "xl:grid-cols-4",
            )}
            data-testid={ATID.figures}
          >
            {view.tiles.map((s) => (
              <ReportTile
                key={s.metric}
                spec={s}
                report={report}
                trend={spark(s.metric)}
                active={s.metric === metric && view.chart.includes(s.metric)}
                {...(view.chart.includes(s.metric) ? { onClick: () => setMetric(s.metric) } : {})}
              />
            ))}
          </div>
          {empty ? (
            <div className={card}>
              <EmptyState
                size="inline"
                headingLevel={3}
                title={t("analytics.reports.noData")}
                body={t("analytics.reports.noDataBody")}
              />
            </div>
          ) : (
            <>
              {topic === "customers" ? (
                <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
                  <CustomerMix report={report} />
                  <DetailsTable report={report} metrics={[]} />
                </div>
              ) : (
                <div
                  className={cx(
                    "grid items-start gap-4",
                    view.chart.length > 0 && "lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]",
                  )}
                >
                  {view.chart.length ? (
                    <TrendCard
                      report={report}
                      prev={compare ? p.data : undefined}
                      metric={metric}
                      metrics={view.chart}
                      onMetric={setMetric}
                    />
                  ) : null}
                  <ShareCard
                    report={report}
                    dims={view.dims}
                    dim={dim}
                    onDim={setDim}
                    metric={view.shareByDim?.[dim] ?? view.shareBy}
                    donut={(view.donut ?? []).includes(dim)}
                  />
                </div>
              )}
              <TopicCard report={report} />
              {topic !== "customers" ? <DetailsTable report={report} metrics={view.chart} /> : null}
            </>
          )}
        </>
      )}
    </>
  );
}

export function ReportsPage() {
  const { topic: raw } = useParams({ strict: false }) as { topic?: string };
  const topic: Topic = (TOPICS as readonly string[]).includes(raw ?? "") ? (raw as Topic) : "sales";
  if (topic === "traffic") return <TrafficPage />;
  return <TopicPage topic={topic} />;
}

function TopicPage({ topic }: { topic: Topic }) {
  const period = usePeriod();
  return (
    <CommercePage
      title={t(`analytics.reports.groups.${groupOf(topic).id}`)}
      anyOf={topic === "profit" ? ["reports.profit.read"] : ["reports.read"]}
      testId={ATID.page}
      canonical="ANALYTICS-001"
    >
      <TopicReport
        key={topic}
        topic={topic}
        from={period.from}
        to={period.to}
        pills={period.pills}
      />
    </CommercePage>
  );
}

// ---------------------------------------------------------------- visitors

const FUNNEL = ["page_view", "product_view", "add_to_cart", "checkout_start", "purchase"] as const;

type ConsentMode = "notice" | "opt_in";
const CONSENT: { mode: ConsentMode; icon: IconName }[] = [
  { mode: "notice", icon: "eye" },
  { mode: "opt_in", icon: "shieldCheck" },
];

/** Visitor tracking (owner, 2026-10-22: redesigned in the site's style): the two counting modes as
 *  choice cards, the privacy promises, and the truth about "only after they agree". */
function ConsentCard() {
  const { orgId, storeId } = useStoreIds();
  const canEdit = useCan("store.settings.update");
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({
    queryKey: analyticsKeys.settings(orgId, storeId),
    queryFn: () => analyticsApi.settings(orgId, storeId),
  });
  const save = useMutation({
    mutationFn: (mode: ConsentMode) => analyticsApi.saveSettings(orgId, storeId, mode),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: analyticsKeys.settings(orgId, storeId) });
      toast.show({ tone: "success", title: t("analytics.traffic.consentSaved") });
    },
    onError: () => toast.show({ tone: "error", title: t("analytics.traffic.consentFailed") }),
  });
  if (!q.data) return null;
  const mode = save.isPending && save.variables ? save.variables : q.data.analytics_consent_mode;
  return (
    <section
      className={card}
      id="analytics-consent"
      aria-labelledby="consent-h"
      data-testid={ATID.consent}
    >
      <header className="flex flex-wrap items-start gap-3">
        <span className="flex size-9 flex-none items-center justify-center rounded-xs ady-bg-brand text-on-primary">
          <Icon name="shield" />
        </span>
        <div className="flex min-w-0 flex-1 flex-col">
          <h2 id="consent-h" className="text-lg font-semibold">
            {t("analytics.traffic.consentTitle")}
          </h2>
          <p className="text-xs text-fg-tertiary">{t("analytics.traffic.consentWhat")}</p>
        </div>
        <ul className="flex flex-wrap gap-1.5" aria-label={t("analytics.traffic.promisesLabel")}>
          {(["noCookies", "noPersonal", "dnt"] as const).map((k) => (
            <li
              key={k}
              className="flex items-center gap-1 rounded-full bg-success-bg px-2 py-0.5 text-2xs font-semibold text-success-fg"
            >
              <Icon name="check" />
              {t(`analytics.traffic.promises.${k}`)}
            </li>
          ))}
        </ul>
      </header>
      <div
        role="radiogroup"
        aria-label={t("analytics.traffic.consentLabel")}
        className="grid gap-2 md:grid-cols-2"
      >
        {CONSENT.map((c) => {
          const on = mode === c.mode;
          return (
            <button
              key={c.mode}
              type="button"
              role="radio"
              aria-checked={on}
              disabled={!canEdit || save.isPending}
              onClick={() => !on && save.mutate(c.mode)}
              className={cx(
                "flex items-start gap-3 rounded-sm border-[1.5px] p-3 text-start disabled:cursor-not-allowed",
                on
                  ? "ady-outline-brand ady-bg-brand-tint"
                  : "border-card bg-surface hover:bg-hover",
              )}
            >
              <span
                className={cx(
                  "flex size-8 flex-none items-center justify-center rounded-xs",
                  on ? "ady-bg-brand text-on-primary" : "bg-sunken text-fg-secondary",
                )}
              >
                <Icon name={c.icon} />
              </span>
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="flex flex-wrap items-center gap-1.5 text-sm font-semibold">
                  <span className={on ? "ady-text-brand" : undefined}>
                    {t(`analytics.traffic.mode.${c.mode}.title`)}
                  </span>
                  {c.mode === "notice" ? (
                    <span className="rounded-full bg-selected px-1.5 text-2xs font-semibold text-link">
                      {t("analytics.traffic.recommended")}
                    </span>
                  ) : null}
                </span>
                <span className="text-xs text-fg-secondary">
                  {t(`analytics.traffic.mode.${c.mode}.body`)}
                </span>
              </span>
              <span
                className={cx(
                  "mt-1 flex size-4 flex-none items-center justify-center rounded-full border-[1.5px]",
                  on ? "border-transparent ady-bg-brand" : "border-control",
                )}
                aria-hidden="true"
              >
                {on ? <span className="size-1.5 rounded-full bg-surface" /> : null}
              </span>
            </button>
          );
        })}
      </div>
      {mode === "opt_in" ? (
        <Banner kind="pending-activation" title={t("analytics.traffic.optInGapTitle")}>
          {t("analytics.traffic.optInGapBody")}
        </Banner>
      ) : null}
      {canEdit ? null : (
        <p className="text-2xs text-fg-tertiary">{t("analytics.traffic.consentOwnerOnly")}</p>
      )}
    </section>
  );
}

/** Visitors: the presets plus Lifetime (the nightly saved days reach back past Analytics Engine's
 *  3 months; the server starts Lifetime at the first saved day). */
const TRAFFIC_PERIODS: readonly Span[] = [...PERIODS, "lifetime"];

/** Visitors and traffic (…/traffic): the same layout as the other reports (tiles, trend, source /
 *  device / page / product, the funnel, details), or the truthful activation state; the consent mode. */
export function TrafficPage() {
  const period = usePeriod(TRAFFIC_PERIODS);
  return (
    <CommercePage
      title={t("analytics.traffic.title")}
      anyOf={["reports.read"]}
      testId={ATID.page}
      canonical="ANALYTICS-001"
    >
      <TopicReport
        topic="traffic"
        from={period.from}
        to={period.to}
        pills={period.pills}
        lifetime={period.lifetime}
      />
      <ConsentCard />
    </CommercePage>
  );
}

// ---------------------------------------------------------------- ranking

export function RankingPage() {
  const { orgId } = useStoreIds();
  const canChange = useCan("org.update");
  const qc = useQueryClient();
  const q = useLeaderboard(orgId);
  const toggle = useMutation({
    mutationFn: (next: boolean) => analyticsApi.setLeaderboard(orgId, next),
    onSuccess: (v) => qc.setQueryData(analyticsKeys.leaderboard(orgId), v),
  });
  const band = q.data?.band?.state === "ready" ? q.data.band : null;
  const BANDS = ["top_10", "top_25", "top_50", "bottom_50"] as const;
  return (
    <CommercePage
      title={t("analytics.reports.groups.operations")}
      anyOf={["reports.read"]}
      testId={ATID.page}
      canonical="ANALYTICS-001"
    >
      <TopicTabs current="ranking" />
      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <section className={card} id="analytics-ranking">
          <CardHead
            title={t("analytics.leaderboard.title")}
            lead={
              q.data?.opted_in
                ? t("analytics.leaderboard.cohortLeadIn")
                : t("analytics.leaderboard.cohortLead")
            }
          />
          {q.isError ? (
            <LoadError error={q.error} onRetry={() => void q.refetch()} />
          ) : q.data ? (
            <LeaderboardBand
              view={q.data}
              canChange={canChange}
              busy={toggle.isPending}
              onToggle={(next) => toggle.mutate(next)}
            />
          ) : (
            <Skeleton className="h-24 w-full" />
          )}
        </section>
        <section className={card} aria-label={t("analytics.leaderboard.ladder")}>
          <CardHead
            title={t("analytics.leaderboard.ladder")}
            lead={t("analytics.leaderboard.ladderLead")}
          />
          <ol className="flex flex-col gap-1.5">
            {BANDS.map((b, i) => {
              const mine = band && (band.orders === b || band.gmv === b);
              return (
                <li
                  key={b}
                  className={cx(
                    "flex items-center gap-2 rounded-xs border px-3 py-2 text-sm",
                    mine ? "ady-outline-brand font-semibold" : "border-subtle",
                  )}
                >
                  <span
                    className="h-2 rounded-full"
                    style={{ width: `${(4 - i) * 14}px`, background: CHART_COLOURS[i] }}
                  />
                  <span className="flex-1">{t(`analytics.leaderboard.bands.${b}`)}</span>
                  {band?.orders === b ? (
                    <Badge>{t("analytics.leaderboard.ordersBand")}</Badge>
                  ) : null}
                  {band?.gmv === b ? <Badge>{t("analytics.leaderboard.gmvBand")}</Badge> : null}
                </li>
              );
            })}
          </ol>
        </section>
      </div>
    </CommercePage>
  );
}

function Badge({ children }: { children: ReactNode }) {
  return (
    <span className="rounded-full bg-selected px-2 text-2xs font-semibold text-link">
      {children}
    </span>
  );
}
